import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getMovieById, getRelatedMovies, categories } from '@/mocks/movies';

const categoryNames: Record<string, string> = {};
categories.forEach((c) => {
  categoryNames[c.slug] = c.name;
});

export default function MovieDetailPage() {
  const { id } = useParams<{ id: string }>();
  const movieId = Number(id);
  const movie = getMovieById(movieId);
  const relatedMovies = movie ? getRelatedMovies(movie) : [];

  useEffect(() => {
    if (movie) {
      document.title = `${movie.title} - 星空影院 | ${movie.year}年${movie.region}${categoryNames[movie.category] || ''}电影免费在线观看`;
    } else {
      document.title = '电影未找到 - 星空影院';
    }
    window.scrollTo(0, 0);
  }, [movie]);

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 border border-background-200/60 mb-4">
              <i className="ri-emotion-sad-line text-foreground-500 text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-100 mb-2">电影未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您要查看的电影不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catDisplayName = categoryNames[movie.category] || movie.category;

  return (
    <>
      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-[1200px] mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 cursor-pointer whitespace-nowrap transition-colors">首页</Link>
              <span>/</span>
              <Link to={`/category/${movie.category}`} className="hover:text-primary-500 cursor-pointer whitespace-nowrap transition-colors">{catDisplayName}电影</Link>
              <span>/</span>
              <span className="text-foreground-300">{movie.title}</span>
            </nav>

            <article className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="flex-shrink-0 w-full sm:w-64 lg:w-72 mx-auto lg:mx-0">
                <img
                  src={movie.coverImage}
                  alt={movie.title}
                  title={`${movie.title} 电影海报 - 星空影院`}
                  className="w-full rounded-lg"
                />
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-2">
                  {movie.title}
                </h1>
                <p className="text-base text-foreground-400 mb-4">{movie.englishTitle}</p>

                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-accent-500/20 border border-accent-500/30">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-xs"></i>
                    </div>
                    <span className="text-sm font-semibold text-accent-400 whitespace-nowrap">{movie.rating}</span>
                  </div>
                  <span className="px-3 py-1.5 rounded-full bg-background-100/80 border border-background-200/60 text-sm text-foreground-300 whitespace-nowrap">
                    {movie.year}
                  </span>
                  <span className="px-3 py-1.5 rounded-full bg-background-100/80 border border-background-200/60 text-sm text-foreground-300 whitespace-nowrap">
                    {movie.region}
                  </span>
                  <span className="px-3 py-1.5 rounded-full bg-background-100/80 border border-background-200/60 text-sm text-foreground-300 whitespace-nowrap">
                    {catDisplayName}
                  </span>
                  <span className="px-3 py-1.5 rounded-full bg-background-100/80 border border-background-200/60 text-sm text-foreground-300 whitespace-nowrap">
                    {movie.duration}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 p-4 rounded-lg bg-background-100/80 border border-background-200/60">
                  <div>
                    <span className="text-xs text-foreground-500">导演</span>
                    <p className="text-sm text-foreground-100 mt-1">{movie.director}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">主演</span>
                    <p className="text-sm text-foreground-100 mt-1">{movie.actors.join(' / ')}</p>
                  </div>
                </div>

                <div className="mb-8">
                  <h2 className="text-lg font-heading font-bold text-foreground-50 mb-3">剧情简介</h2>
                  <p className="text-sm text-foreground-400 leading-relaxed">{movie.description}</p>
                </div>

                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-play-circle-fill text-base"></i>
                  </div>
                  立即观看
                </Link>
              </div>
            </article>

            {relatedMovies.length > 0 && (
              <section className="mt-14">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-50">
                    相关推荐
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {relatedMovies.map((rm) => (
                    <Link
                      key={rm.id}
                      to={`/movie/${rm.id}`}
                      className="group flex flex-col cursor-pointer"
                    >
                      <div className="relative overflow-hidden rounded-lg aspect-[2/3] mb-3">
                        <img
                          src={rm.coverImage}
                          alt={rm.title}
                          title={`${rm.title} - 相关推荐`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute top-2 right-2">
                          <div className="flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-background-50/80 text-foreground-950 text-[10px] font-semibold whitespace-nowrap">
                            <i className="ri-star-fill text-primary-500 text-[8px]"></i>
                            {rm.rating}
                          </div>
                        </div>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-100 group-hover:text-primary-500 transition-colors truncate">
                        {rm.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{rm.year}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: movie.title,
            description: movie.description,
            dateCreated: `${movie.year}`,
            director: {
              '@type': 'Person',
              name: movie.director,
            },
            actor: movie.actors.map((a) => ({
              '@type': 'Person',
              name: a,
            })),
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: movie.rating,
              bestRating: '10',
              worstRating: '0',
            },
            countryOfOrigin: {
              '@type': 'Country',
              name: movie.region,
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://xingkongcinema.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: `${catDisplayName}电影`,
                item: `https://xingkongcinema.com/category/${movie.category}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: movie.title,
              },
            ],
          }),
        }}
      />
    </>
  );
}